import { z } from "zod";

export const RoleSchema = z.enum([
  "household_admin",
  "adult",
  "child",
  "young_child",
  "executive_assistant",
  "household_helper",
  "babysitter",
  "relative",
  "vendor",
  "household_display",
]);

export const VisibilitySchema = z.enum([
  "shared",
  "adults",
  "private",
  "protected",
  "child_limited",
  "helper_limited",
]);

export const SensitivitySchema = z.enum([
  "normal",
  "financial",
  "health",
  "relationship",
  "intimacy",
  "location",
  "security",
  "credential_reference",
]);

export const PermissionActionSchema = z.enum([
  "view",
  "create",
  "update",
  "complete",
  "approve",
  "delegate",
  "manage_access",
  "operate_device",
]);

export const ProposalStateSchema = z.enum([
  "pending",
  "approved",
  "edited_approved",
  "rejected",
  "superseded",
  "applied",
  "failed",
]);

export const InboxStatusSchema = z.enum([
  "received",
  "uploading",
  "stored",
  "extracting",
  "transcribing",
  "analyzing",
  "needs_review",
  "partially_applied",
  "applied",
  "archived",
  "failed",
  "quarantined",
]);

export const ProposalListItemSchema = z.object({
  id: z.string().min(1),
  inbox_item_id: z.string().min(1),
  proposal_type: z.string().min(1),
  title: z.string().min(1),
  payload_json: z.string(),
  confidence: z.number().min(0).max(1),
  state: ProposalStateSchema,
  evidence_excerpt: z.string(),
  reason: z.string().nullable(),
  sensitivity: SensitivitySchema,
  created_at: z.string(),
  source_title: z.string().min(1),
  source_status: InboxStatusSchema,
  source_visibility: VisibilitySchema,
  source_retention: z.enum([
    "ephemeral",
    "active_operational",
    "household_knowledge",
    "protected_private",
    "do_not_retain",
  ]),
  source_created_at: z.string(),
  suggested_owner_name: z.string().nullable(),
});

export const ProposalListResponseSchema = z.object({
  proposals: z.array(ProposalListItemSchema),
});

export const AiExtractionSchema = z.object({
  source_summary: z.string().max(2000),
  people_mentioned: z.array(
    z.object({
      name: z.string().min(1),
      matched_person_id: z.string().nullable(),
      confidence: z.number().min(0).max(1),
    }),
  ),
  time_candidates: z.array(
    z.object({
      text: z.string().min(1),
      resolved_at: z.string().datetime().nullable(),
      confidence: z.number().min(0).max(1),
      needs_confirmation: z.boolean(),
    }),
  ),
  observations: z.array(
    z.object({
      text: z.string().min(1),
      confidence: z.number().min(0).max(1),
      sensitivity: SensitivitySchema.or(z.literal("household")),
    }),
  ),
  proposals: z.array(
    z.object({
      type: z.enum([
        "task",
        "event",
        "expense",
        "future_purchase",
        "project",
        "decision",
        "person_update",
        "maintenance",
        "meal",
        "grocery_item",
        "clarification",
        "rhythm",
        "delegation",
      ]),
      title: z.string().min(1),
      suggested_owner_person_id: z.string().nullable().optional(),
      due_at: z.string().datetime().nullable().optional(),
      confidence: z.number().min(0).max(1),
      requires_confirmation: z.boolean(),
      evidence: z.string().min(1),
    }),
  ),
  facts: z.array(z.unknown()),
  unresolved_questions: z.array(z.string()),
  retention_recommendation: z.enum([
    "ephemeral",
    "active_operational",
    "household_knowledge",
    "protected_private",
    "do_not_retain",
  ]),
  safety_flags: z.array(z.string()),
});

export type Role = z.infer<typeof RoleSchema>;
export type Visibility = z.infer<typeof VisibilitySchema>;
export type Sensitivity = z.infer<typeof SensitivitySchema>;
export type PermissionAction = z.infer<typeof PermissionActionSchema>;
export type AiExtraction = z.infer<typeof AiExtractionSchema>;
export type ProposalListItem = z.infer<typeof ProposalListItemSchema>;
